import {DomInjectionType} from "./script-injection";
import {isBrowser} from "@montana/helpers/browser.helper";


function scriptExistsInDOM(src: string): boolean {
    return document.querySelector(`script[src="${src}"]`) != null;
}

function injectScript(scriptEl: HTMLScriptElement, injectionType: DomInjectionType): void {
    switch (injectionType) {
        case DomInjectionType.BODY_FIRST: {
            document.body.insertBefore(scriptEl, document.body.firstChild);
            break;
        }
        case DomInjectionType.HEAD_FIRST: {
            document.head.insertBefore(scriptEl, document.body.firstChild);
            break;
        }
        case DomInjectionType.HEAD_APPEND: {
            document.head.appendChild(scriptEl);
            break;
        }
        case DomInjectionType.BODY_APPEND:
        default: {
            document.body.appendChild(scriptEl);
            break;
        }
    }
}

interface ILoadScriptOptions extends ILoadScriptAsyncOptions {
    successCallback?: (event?: Event) => void;
    errorCallback?: (event?: Event) => void;
}

interface ILoadScriptAsyncOptions {
    async?: boolean;
    defer?: boolean;
    injectAt?: DomInjectionType;
    attributes?: { [key: string]: string };
    force?: boolean;
}

export function insertJavaScript(javaScriptString: string) {
    const scriptEl: HTMLScriptElement = document.createElement("script");
    scriptEl.textContent = javaScriptString;

    injectScript(scriptEl, DomInjectionType.HEAD_APPEND);
}

/**
 * Inserts a script element to the DOM, and resolve if the script loads successfully.
 * @param src The URL of the script to load.
 * @param options Options for customizing the way the script is loaded.
 * @returns A `Promise` which resolves to `true` when the script is successfully loaded -
 * otherwise it resolves to `false`.
 */
export async function loadScriptAsync(src: string, options: ILoadScriptAsyncOptions = {}): Promise<boolean> {
    // If NOT in a browser-context, reject the promise.
    if (!isBrowser) return Promise.resolve(false);

    // If script has already been added, resolve promise immediately.
    if (!options.force && scriptExistsInDOM(src)) return Promise.resolve(true);

    // De-struct options (or default values for options).
    const {
        async = true,
        defer = false,
        injectAt = DomInjectionType.BODY_APPEND,
        attributes = {},
    } = options;

    // Initiate script element.
    const scriptElement = document.createElement("script");
    scriptElement.async = async;
    scriptElement.defer = defer;
    scriptElement.src = src;

    // Insert custom attributes from options.
    Object.keys(attributes).forEach(key => scriptElement.setAttribute(key, attributes[key]));

    // Register (and un-register) callbacks
    const callbacks = new Promise<boolean>((resolve) => {
        const onComplete = (e: Event) => {
            scriptElement.removeEventListener('load', onComplete);
            scriptElement.removeEventListener('error', onComplete);
            resolve(e.type === 'load');
        }

        scriptElement.addEventListener('load', onComplete);
        scriptElement.addEventListener('error', onComplete);
    });

    // Register script
    injectScript(scriptElement, injectAt);

    return callbacks;
}

/**
 * Inserts a script element to the DOM.
 * @deprecated Use `loadScriptAsync` instead.
 * @param src The URL of the script to load.
 * @param options Options for customizing the way the script is loaded.
 */
export function loadScript(src: string, options: ILoadScriptOptions = {}): void {
    const {
        successCallback,
        errorCallback,
    } = options;

    loadScriptAsync(src, options).then(success => {
        success && successCallback && successCallback();
        !success && errorCallback && errorCallback();
    });
}
